import {
  ArrowPathIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
} from "@heroicons/react/24/solid";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TextInput, CheckboxInput } from "../../components/Inputs";
import { PageHeader } from "../../components/Layouts";
import { ActiveFilter, PrimaryTable } from "../../components/Tables";
import { useLoaderStore } from "../../context";
import { useAlert, useApi } from "../../hooks";
import constants from "../../utils/constants";
import { switchSortDirection } from "../../utils/helpers";
import { PrimaryButton } from "../../components/Buttons";
import { Popover } from "@headlessui/react";
import { useActiveTenant } from "../../context/useSystemTenantStore";
import useDevManager from "../../context/useDevManager";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const EMPTY_FILTERS = {
  activeOnly: false,
  firstName: undefined,
  lastName: undefined,
  email: undefined,
};

function getFiltersFromSearchParams(searchParams) {
  return {
    ...EMPTY_FILTERS,
    activeOnly: searchParams.get("activeOnly") === "true",
    firstName: searchParams.get("firstName") || undefined,
    lastName: searchParams.get("lastName") || undefined,
    email: searchParams.get("email") || undefined,
  };
}

function buildSearchParams({
  filters,
  currentPage,
  displayCount,
  sortDirection,
  sortField,
}) {
  const params = {};

  if (currentPage && currentPage !== 1) params.pageNumber = String(currentPage);
  if (displayCount) params.pageSize = String(displayCount);
  if (sortDirection) params.sortDirection = sortDirection;
  if (sortField) params.sortField = sortField;

  if (filters.activeOnly) params.activeOnly = "true";
  if (filters.firstName) params.firstName = filters.firstName;
  if (filters.lastName) params.lastName = filters.lastName;
  if (filters.email) params.email = filters.email;

  return params;
}

export default function SystemTenantCustomers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const alert = useAlert();
  const { post, fetch } = useApi();
  const { setShowLoader } = useLoaderStore();
  const activeSystemTenant = useActiveTenant();
  const { IS_DEV } = useDevManager();

  const currentPage = Number(searchParams.get("pageNumber")) || 1;
  const displayCount =
    Number(searchParams.get("pageSize")) || constants.DISPLAY_PER_PAGE || 25;
  const sortDirection =
    searchParams.get("sortDirection") || constants.SORT_DIRECTIONS.ASCENDING;
  const sortField = searchParams.get("sortField") || "id";

  const activeFilters = useMemo(
    () => getFiltersFromSearchParams(searchParams),
    [searchParams]
  );

  const [stagedFilters, setStagedFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    ...getFiltersFromSearchParams(searchParams),
  }));

  useEffect(() => {
    setStagedFilters(activeFilters);
  }, [activeFilters]);

  const payloadFilters = useMemo(() => {
    if (!activeSystemTenant?.id) return null;

    return {
      pageNumber: currentPage,
      pageSize: displayCount,
      sortDirection,
      sortField,
      systemTenantId: activeSystemTenant.id,
      ...activeFilters,
    };
  }, [
    currentPage,
    displayCount,
    sortDirection,
    sortField,
    activeFilters,
    activeSystemTenant?.id,
  ]);

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["systemTenantCustomers", payloadFilters],
    queryFn: () => fetch("systemTenant/customers", payloadFilters),
    enabled: !!payloadFilters,
  });

  const updateTableParams = (updates) => {
    const nextFilters = updates.filters ?? activeFilters;
    const nextCurrentPage = updates.currentPage ?? currentPage;
    const nextDisplayCount = updates.displayCount ?? displayCount;
    const nextSortDirection = updates.sortDirection ?? sortDirection;
    const nextSortField = updates.sortField ?? sortField;

    setSearchParams(
      buildSearchParams({
        filters: nextFilters,
        currentPage: nextCurrentPage,
        displayCount: nextDisplayCount,
        sortDirection: nextSortDirection,
        sortField: nextSortField,
      })
    );
  };

  useEffect(() => {
      if(error) {
          alert(
          "Error",
          error.data.message || "Failed to fetch customers",
              "error");

      }
  }, [error, alert])

  const applyFilters = (nextFilters) => {
    setStagedFilters(nextFilters);
    updateTableParams({
      filters: nextFilters,
      currentPage: 1,
    });
  };

  const setCurrentPage = (page) => {
    updateTableParams({ currentPage: page });
  };

  const setDisplayCount = (count) => {
    updateTableParams({
      displayCount: count,
      currentPage: 1,
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      updateTableParams({
        currentPage: 1,
        sortDirection: switchSortDirection(sortDirection),
      });
    } else {
      updateTableParams({
        currentPage: 1,
        sortField: field,
      });
    }
  };

  const toggleActiveStatus = (account) => {
    setShowLoader(true);
    post(`account/toggleAccountActive/${account.id}`)
      .then((res) => {
        alert(
          "Success",
          res.message || "Account active status toggled",
          "success"
        );
        queryClient.invalidateQueries({
          queryKey: ["systemTenantCustomers"],
        });
      })
      .catch((err) => {
        alert(
          "Error",
          err.data.message || "Account active status could not be toggled",
          "error"
        );
        console.error(err);
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mt-4 px-4">
        <PageHeader title="Customers" ignoreSpacing />
      </div>

      {/* Filter section */}
      <CustomerFilterSection
        stagedFilters={stagedFilters}
        setStagedFilters={setStagedFilters}
        activeFilters={activeFilters}
        setActiveFilters={applyFilters}
      />

      {/* Table */}
      <div className="px-2">
        <PrimaryTable
          isLoading={isLoading}
          totalCount={data?.totalCount}
          displayCount={displayCount}
          currentPage={currentPage}
          setDisplayCount={setDisplayCount}
          setCurrentPage={setCurrentPage}
          header={
            <PrimaryTable.Header>
              {IS_DEV && (
                <PrimaryTable.HeaderColumn
                  display="Id"
                  onSort={() => handleSort("id")}
                  isSortingBy={sortField === "id"}
                  sortDirection={sortDirection}
                />
              )}
              <PrimaryTable.HeaderColumn
                display="First Name"
                onSort={() => handleSort("firstName")}
                isSortingBy={sortField === "firstName"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn
                display="Last Name"
                onSort={() => handleSort("lastName")}
                isSortingBy={sortField === "lastName"}
                sortDirection={sortDirection}
              />
              <PrimaryTable.HeaderColumn display="Email" />
              <PrimaryTable.HeaderColumn display="Phone" />
              <PrimaryTable.HeaderColumn display="" small />
            </PrimaryTable.Header>
          }
        >
          {data?.list?.map((x, i) => (
            <PrimaryTable.Row
              key={`${x.id}-${i}`}
              index={i}
              onClick={() => {
                if (!x.deactivatedAt) {
                  navigate(`/customers/${x.id}`);
                }
              }}
            >
              {IS_DEV && <PrimaryTable.Cell>{x.id}</PrimaryTable.Cell>}
              <PrimaryTable.Cell>{x.firstName}</PrimaryTable.Cell>
              <PrimaryTable.Cell>{x.lastName}</PrimaryTable.Cell>
              <PrimaryTable.Cell>{x.email}</PrimaryTable.Cell>
              <PrimaryTable.Cell>{x.mobilePhone}</PrimaryTable.Cell>
              <PrimaryTable.Cell>
                <div className="flex justify-center">
                  {x.deactivatedAt ? (
                    <ArrowPathIcon
                      title="Activate"
                      className="w-6 mx-1 text-white bg-green-500 rounded-md p-0.5 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleActiveStatus(x);
                      }}
                    />
                  ) : (
                    <XMarkIcon
                      title="Deactivate"
                      className="w-6 mx-1 text-white bg-delete rounded-md p-0.5 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleActiveStatus(x);
                      }}
                    />
                  )}
                </div>
              </PrimaryTable.Cell>
            </PrimaryTable.Row>
          ))}
        </PrimaryTable>
      </div>
    </div>
  );
}

const CustomerFilterSection = ({
  stagedFilters,
  setStagedFilters,
  activeFilters,
  setActiveFilters,
}) => {
  return (
    <div className="relative mb-4 rounded-xs shadow-sm bg-white p-4">
      <Popover as="div" className="relative">
        {() => (
          <>
            <Popover.Button className="flex items-center text-sm bg-gray-100 px-3 py-2 rounded-sm hover:bg-gray-200 relative focus:outline-none">
              <PlusIcon className="w-4 mr-1" />
              FILTERS
            </Popover.Button>
            <Popover.Panel
              as="div"
              className="absolute grid gap-y-4 z-50 w-full sm:w-1/3 origin-bottom-right bg-white rounded-md p-4 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none mt-1"
            >
              <div>
                <CheckboxInput
                  name="active-only"
                  label="Show only active customers"
                  checked={stagedFilters.activeOnly === true}
                  onChange={() =>
                    setStagedFilters({
                      ...stagedFilters,
                      activeOnly: stagedFilters.activeOnly ? false : true,
                    })
                  }
                />
              </div>
              <div>
                <TextInput
                  label="First Name"
                  placeholder="Enter first name"
                  value={stagedFilters.firstName}
                  onChange={(e) =>
                    setStagedFilters({
                      ...stagedFilters,
                      firstName: e.target.value || undefined,
                    })
                  }
                />
              </div>
              <div>
                <TextInput
                  label="Last Name"
                  placeholder="Enter last name"
                  value={stagedFilters.lastName}
                  onChange={(e) =>
                    setStagedFilters({
                      ...stagedFilters,
                      lastName: e.target.value || undefined,
                    })
                  }
                />
              </div>
              <div>
                <TextInput
                  label="Email"
                  placeholder="Enter email"
                  value={stagedFilters.email}
                  onChange={(e) =>
                    setStagedFilters({
                      ...stagedFilters,
                      email: e.target.value || undefined,
                    })
                  }
                />
              </div>
              <PrimaryButton
                text="Apply filters"
                onClick={() => setActiveFilters(stagedFilters)}
              />
            </Popover.Panel>
          </>
        )}
      </Popover>

      {/* Selected filters */}
      {Object.values(activeFilters).find(
        (val) => val !== undefined && val !== false
      ) ? (
        <div className="flex items-center space-x-2 pt-6 text-sm">
          <div>Selected:</div>
          <ActiveFilter
            display="Show active only"
            value={
              activeFilters.activeOnly ? (
                <CheckIcon className="w-4 text-green-600" />
              ) : undefined
            }
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                activeOnly: false,
              })
            }
          />
          <ActiveFilter
            display="First Name"
            value={activeFilters.firstName}
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                firstName: undefined,
              })
            }
          />
          <ActiveFilter
            display="Last Name"
            value={activeFilters.lastName}
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                lastName: undefined,
              })
            }
          />
          <ActiveFilter
            display="Email"
            value={activeFilters.email}
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                email: undefined,
              })
            }
          />
          <div
            className="cursor-pointer bg-gray-100 px-3 py-2 text-xs hover:bg-gray-200"
            onClick={() => {
              setActiveFilters({ ...EMPTY_FILTERS });
            }}
          >
            Clear all
          </div>
        </div>
      ) : (
        <div className="pt-6 text-sm text-gray-400">No active filters</div>
      )}
    </div>
  );
};